import React, { useState } from 'react';
import { motion } from 'framer-motion';
import DashboardLayout from '../../components/dashboard/DashboardLayout';
import SafeIcon from '../../common/SafeIcon';
import Button from '../../components/common/Button';
import * as FiIcons from 'react-icons/fi';

const {
  FiGlobe,
  FiEye,
  FiLayers,
  FiGrid,
  FiEdit,
  FiImage,
  FiType,
  FiBox,
  FiColumns,
  FiMenu,
  FiLink,
  FiSave,
  FiDownload,
  FiPlus,
  FiSettings,
  FiX,
  FiChevronUp,
  FiChevronDown,
  FiMonitor,
  FiSmartphone,
  FiTablet,
  FiUpload
} = FiIcons;

const WebsiteBuilder = () => {
  const [activeTab, setActiveTab] = useState('editor');
  const [selectedElement, setSelectedElement] = useState(null);
  const [selectedPage, setSelectedPage] = useState('home');
  const [viewportSize, setViewportSize] = useState('desktop');
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  
  const pages = [
    { id: 'home', name: 'Home', path: '/' },
    { id: 'portfolio', name: 'Portfolio', path: '/portfolio' },
    { id: 'about', name: 'About Me', path: '/about' },
    { id: 'contact', name: 'Contact', path: '/contact' },
    { id: 'blog', name: 'Blog', path: '/blog' }
  ];

  const templates = [
    {
      id: 'minimal',
      name: 'Minimal',
      thumbnail: 'https://images.unsplash.com/photo-1507842217343-583bb7270b66?w=300&h=200&fit=crop',
      category: 'portfolio'
    },
    {
      id: 'bold',
      name: 'Bold & Modern',
      thumbnail: 'https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=300&h=200&fit=crop',
      category: 'portfolio'
    },
    {
      id: 'classic',
      name: 'Classic',
      thumbnail: 'https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=300&h=200&fit=crop',
      category: 'portfolio'
    },
    {
      id: 'dark',
      name: 'Dark Mode',
      thumbnail: 'https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=300&h=200&fit=crop',
      category: 'portfolio'
    }
  ];

  const elementTypes = [
    { id: 'heading', name: 'Heading', icon: FiType },
    { id: 'text', name: 'Text Block', icon: FiType },
    { id: 'image', name: 'Image', icon: FiImage },
    { id: 'gallery', name: 'Gallery', icon: FiGrid },
    { id: 'button', name: 'Button', icon: FiBox },
    { id: 'divider', name: 'Divider', icon: FiMenu },
    { id: 'spacer', name: 'Spacer', icon: FiLayers },
    { id: 'columns', name: '2 Columns', icon: FiColumns }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'editor':
        return (
          <div className="flex h-[calc(100vh-12rem)]">
            {/* Left Sidebar */}
            {isSidebarOpen && (
              <motion.div 
                initial={{ width: 0, opacity: 0 }}
                animate={{ width: 280, opacity: 1 }}
                exit={{ width: 0, opacity: 0 }}
                className="bg-white dark:bg-dark-800 border-r border-gray-200 dark:border-gray-700 overflow-y-auto"
              >
                <div className="p-4">
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
                    Elements
                  </h3>
                  
                  <div className="grid grid-cols-2 gap-2 mb-6">
                    {elementTypes.map((element) => (
                      <button
                        key={element.id}
                        className="flex flex-col items-center justify-center p-3 bg-gray-50 dark:bg-gray-750 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-700 transition-colors"
                        onClick={() => setSelectedElement(element)}
                      >
                        <SafeIcon icon={element.icon} className="w-6 h-6 text-gray-700 dark:text-gray-300 mb-2" />
                        <span className="text-xs text-gray-700 dark:text-gray-300">
                          {element.name}
                        </span>
                      </button>
                    ))}
                  </div>
                  
                  <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mb-4">
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
                      Pages
                    </h3>
                    
                    <div className="space-y-1">
                      {pages.map((page) => (
                        <button
                          key={page.id}
                          className={`w-full flex items-center justify-between px-3 py-2 rounded-lg ${
                            selectedPage === page.id 
                              ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400' 
                              : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
                          }`}
                          onClick={() => setSelectedPage(page.id)}
                        >
                          <span className="flex items-center">
                            <SafeIcon icon={FiGlobe} className="w-4 h-4 mr-2" />
                            {page.name}
                          </span>
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {page.path}
                          </span>
                        </button>
                      ))}
                      
                      <button className="w-full flex items-center justify-center px-3 py-2 mt-2 border border-dashed border-gray-300 dark:border-gray-600 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-750">
                        <SafeIcon icon={FiPlus} className="w-4 h-4 mr-2" />
                        Add New Page
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Main Editor Area */}
            <div className="flex-1 flex flex-col">
              {/* Editor Toolbar */}
              <div className="bg-white dark:bg-dark-800 border-b border-gray-200 dark:border-gray-700 p-2 flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                    className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400"
                  >
                    <SafeIcon icon={isSidebarOpen ? FiChevronUp : FiChevronDown} className="w-5 h-5" />
                  </button>
                  
                  <select
                    value={selectedPage}
                    onChange={(e) => setSelectedPage(e.target.value)}
                    className="bg-gray-100 dark:bg-gray-700 border-0 rounded-lg text-sm text-gray-700 dark:text-gray-300 py-1 px-3"
                  >
                    {pages.map((page) => (
                      <option key={page.id} value={page.id}>
                        {page.name}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className="flex items-center space-x-2">
                  <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-1 flex">
                    <button
                      onClick={() => setViewportSize('desktop')}
                      className={`p-1 rounded ${
                        viewportSize === 'desktop'
                          ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                          : 'text-gray-500 dark:text-gray-400'
                      }`}
                      title="Desktop View"
                    >
                      <SafeIcon icon={FiMonitor} className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => setViewportSize('tablet')}
                      className={`p-1 rounded ${
                        viewportSize === 'tablet'
                          ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                          : 'text-gray-500 dark:text-gray-400'
                      }`}
                      title="Tablet View"
                    >
                      <SafeIcon icon={FiTablet} className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => setViewportSize('mobile')}
                      className={`p-1 rounded ${
                        viewportSize === 'mobile'
                          ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                          : 'text-gray-500 dark:text-gray-400'
                      }`}
                      title="Mobile View"
                    >
                      <SafeIcon icon={FiSmartphone} className="w-5 h-5" />
                    </button>
                  </div>
                  
                  <Button variant="outline" size="sm" icon={FiEye}>
                    Preview
                  </Button>
                  
                  <Button size="sm" icon={FiSave}>
                    Save
                  </Button>
                </div>
              </div>
              
              {/* Editor Canvas */}
              <div className="flex-1 bg-gray-100 dark:bg-gray-900 overflow-auto p-4 flex justify-center">
                <div 
                  className={`bg-white dark:bg-dark-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 ${
                    viewportSize === 'desktop' ? 'w-full max-w-6xl' :
                    viewportSize === 'tablet' ? 'w-[768px]' :
                    'w-[375px]'
                  } h-full transition-all duration-300`}
                >
                  {selectedPage === 'home' ? (
                    <div className="p-4 h-full">
                      <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-8 h-full flex flex-col items-center justify-center text-center">
                        <div className="w-16 h-16 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center mb-4">
                          <SafeIcon icon={FiLayers} className="w-8 h-8 text-primary-600 dark:text-primary-400" />
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                          Start Building Your Website
                        </h3>
                        <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-md">
                          Drag and drop elements from the left sidebar to build your custom website, or choose a template to get started quickly.
                        </p>
                        <Button
                          variant="outline"
                          size="sm"
                          icon={FiGrid}
                          onClick={() => setActiveTab('templates')}
                        >
                          Choose a Template
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="p-4 h-full">
                      <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-8 h-full flex items-center justify-center">
                        <p className="text-gray-500 dark:text-gray-400">
                          {pages.find(p => p.id === selectedPage)?.name} page content will appear here
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Right Sidebar (Properties Panel) */}
            {selectedElement && (
              <motion.div 
                initial={{ width: 0, opacity: 0 }}
                animate={{ width: 300, opacity: 1 }}
                exit={{ width: 0, opacity: 0 }}
                className="bg-white dark:bg-dark-800 border-l border-gray-200 dark:border-gray-700 overflow-y-auto"
              >
                <div className="p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                      {selectedElement.name} Properties
                    </h3>
                    <button
                      onClick={() => setSelectedElement(null)}
                      className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                    >
                      <SafeIcon icon={FiX} className="w-5 h-5" />
                    </button>
                  </div>
                  
                  <div className="space-y-4">
                    {selectedElement.id === 'heading' && (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Text
                          </label>
                          <input
                            type="text"
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-dark-700 text-gray-900 dark:text-white"
                            defaultValue="Heading Text"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Heading Level
                          </label>
                          <select className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-dark-700 text-gray-900 dark:text-white">
                            <option value="h1">H1</option>
                            <option value="h2">H2</option>
                            <option value="h3">H3</option>
                            <option value="h4">H4</option>
                            <option value="h5">H5</option>
                            <option value="h6">H6</option>
                          </select>
                        </div>
                      </>
                    )}
                    
                    {selectedElement.id === 'image' && (
                      <>
                        <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-4 flex flex-col items-center justify-center">
                          <SafeIcon icon={FiImage} className="w-12 h-12 text-gray-400 mb-2" />
                          <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                            Drag and drop an image or click to upload
                          </p>
                          <Button variant="outline" size="sm" icon={FiUpload}>
                            Upload
                          </Button>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Alt Text
                          </label>
                          <input
                            type="text"
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-dark-700 text-gray-900 dark:text-white"
                            placeholder="Describe this image..."
                          />
                        </div>
                      </>
                    )}
                    
                    {/* Common Properties for All Elements */}
                    <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                        Layout & Spacing
                      </h4>
                      <div className="grid grid-cols-2 gap-3 mb-4">
                        <div>
                          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                            Width
                          </label>
                          <select className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-dark-700 text-gray-900 dark:text-white">
                            <option value="full">Full Width</option>
                            <option value="auto">Auto</option>
                            <option value="custom">Custom</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                            Alignment
                          </label>
                          <select className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-dark-700 text-gray-900 dark:text-white">
                            <option value="left">Left</option>
                            <option value="center">Center</option>
                            <option value="right">Right</option>
                          </select>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-4 gap-3 mb-4">
                        <div>
                          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                            Top
                          </label>
                          <input
                            type="number"
                            className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-dark-700 text-gray-900 dark:text-white"
                            defaultValue="0"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                            Right
                          </label>
                          <input
                            type="number"
                            className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-dark-700 text-gray-900 dark:text-white"
                            defaultValue="0"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                            Bottom
                          </label>
                          <input
                            type="number"
                            className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-dark-700 text-gray-900 dark:text-white"
                            defaultValue="0"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                            Left
                          </label>
                          <input
                            type="number"
                            className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-dark-700 text-gray-900 dark:text-white"
                            defaultValue="0"
                          />
                        </div>
                      </div>
                    </div>
                    
                    <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                        Advanced
                      </h4>
                      <div>
                        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                          Element ID
                        </label>
                        <input
                          type="text"
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-dark-700 text-gray-900 dark:text-white"
                          placeholder="Optional ID..."
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </div>
        );
        
      case 'templates':
        return (
          <div className="h-[calc(100vh-12rem)] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                Website Templates
              </h2>
              <Button
                variant="outline"
                onClick={() => setActiveTab('editor')}
              >
                Back to Editor
              </Button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {templates.map((template) => (
                <div
                  key={template.id}
                  className="bg-white dark:bg-dark-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden hover:shadow-md transition-shadow"
                >
                  <div className="aspect-video bg-gray-200 dark:bg-gray-700 relative">
                    <img
                      src={template.thumbnail}
                      alt={template.name}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-30 transition-opacity flex items-center justify-center opacity-0 hover:opacity-100">
                      <Button size="sm" icon={FiEye}>
                        Preview
                      </Button>
                    </div>
                  </div>
                  <div className="p-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        {template.name}
                      </h3>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400 capitalize">
                        {template.category}
                      </span>
                    </div>
                    <div className="mt-4 flex justify-end">
                      <Button 
                        onClick={() => setActiveTab('editor')}
                      >
                        Use Template
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      
      case 'settings':
        return (
          <div className="h-[calc(100vh-12rem)] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                Website Settings
              </h2>
              <Button
                variant="outline"
                onClick={() => setActiveTab('editor')}
              >
                Back to Editor
              </Button>
            </div>
            
            <div className="bg-white dark:bg-dark-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                General Settings
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Website Title
                  </label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-dark-700 text-gray-900 dark:text-white"
                    defaultValue="My Photography Portfolio"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Tagline
                  </label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-dark-700 text-gray-900 dark:text-white"
                    defaultValue="Capturing moments that last forever"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Favicon
                  </label>
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 border border-gray-300 dark:border-gray-600 rounded-lg flex items-center justify-center bg-gray-100 dark:bg-gray-700">
                      <SafeIcon icon={FiImage} className="w-6 h-6 text-gray-400" />
                    </div>
                    <Button variant="outline" size="sm" icon={FiUpload}>
                      Upload
                    </Button>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-white dark:bg-dark-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                SEO Settings
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Meta Description
                  </label>
                  <textarea
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-dark-700 text-gray-900 dark:text-white"
                    defaultValue="Professional photography portfolio showcasing portraits, landscapes, and event photography. Based in San Francisco, CA."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Keywords
                  </label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-dark-700 text-gray-900 dark:text-white"
                    defaultValue="photography, portfolio, portrait, landscape, professional photographer"
                  />
                </div>
              </div>
            </div>
            
            <div className="bg-white dark:bg-dark-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Domain Settings
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Current Domain
                  </label>
                  <div className="flex items-center">
                    <input
                      type="text"
                      className="flex-1 px-3 py-2 border border-r-0 border-gray-300 dark:border-gray-600 rounded-l-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-dark-700 text-gray-900 dark:text-white"
                      value="your-portfolio"
                      disabled
                    />
                    <span className="px-3 py-2 border border-l-0 border-gray-300 dark:border-gray-600 rounded-r-lg bg-gray-50 dark:bg-gray-750 text-gray-500 dark:text-gray-400">
                      .tasveer.com
                    </span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Custom Domain
                  </label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-dark-700 text-gray-900 dark:text-white"
                    placeholder="www.yourphotography.com"
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Pro plan required for custom domains
                  </p>
                </div>
                <div className="pt-4">
                  <Button variant="outline">
                    Connect Domain
                  </Button>
                </div>
              </div>
            </div>
          </div>
        );
        
      default:
        return (
          <div className="text-center p-12 text-gray-600 dark:text-gray-400">
            Select a tab to view content
          </div>
        );
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              Website Builder
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Create and customize your photography portfolio website
            </p>
          </div>
          <div className="mt-4 sm:mt-0 flex space-x-3">
            <Button
              variant={activeTab === 'editor' ? 'primary' : 'outline'}
              icon={FiEdit}
              onClick={() => setActiveTab('editor')}
            >
              Editor
            </Button>
            <Button
              variant={activeTab === 'templates' ? 'primary' : 'outline'}
              icon={FiGrid}
              onClick={() => setActiveTab('templates')}
            >
              Templates
            </Button>
            <Button
              variant={activeTab === 'settings' ? 'primary' : 'outline'}
              icon={FiSettings}
              onClick={() => setActiveTab('settings')}
            >
              Settings
            </Button>
          </div>
        </div>

        {/* Current Site Preview */}
        <div className="bg-white dark:bg-dark-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center">
                <SafeIcon icon={FiGlobe} className="w-5 h-5 text-primary-600 dark:text-primary-400" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Your Portfolio Site
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  your-portfolio.tasveer.com
                </p>
              </div>
            </div>
            <div className="flex space-x-2">
              <Button variant="outline" size="sm" icon={FiEye}>
                Visit Site
              </Button>
              <Button size="sm" icon={FiLink}>
                Share
              </Button>
            </div>
          </div>
          
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
            <img 
              src="https://images.unsplash.com/photo-1507842217343-583bb7270b66?w=1200&h=400&fit=crop"
              alt="Website preview"
              className="w-full h-48 object-cover"
            />
          </div>
        </div>

        {/* Tab Content */}
        <div className="bg-gray-50 dark:bg-dark-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
          {renderTabContent()}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default WebsiteBuilder;